import React, { useMemo, useState } from "react";
import { useFormik } from "formik";
import { format } from "date-fns";
import * as Yup from "yup";
import {
  Box,
  VStack,
  HStack,
  ButtonPrimary,
  Error,
} from "../../../components/utils";
import { Input, Label, Radio } from "../../../components/shared/InputField";
import { useGetGrantsForBlackScholes } from "../../../queries/blackScholes";
import {
  BlackScholesGrantDetails,
  BlackScholesV2,
  EXPECTED_LIFE_CALCULATION_METHOD_LABELS,
} from "../../../types/blackScholes";
import GenericFilterBar, {
  GenericFilterType,
} from "../../companyProfile/utils/Filters";
import { CollapsibleComponent } from "../../../components/shared/Collapsible";
import { NameAndId } from "../../../types/Transaction";
import { SwitchButton } from "../../../components/shared/SwitchButton";

interface GrantScopeProps {
  grantScopeData?: BlackScholesV2;
  onNext: (data: BlackScholesV2) => void;
}

export const GrantScope: React.FC<GrantScopeProps> = ({
  grantScopeData,
  onNext,
}) => {
  const { data: grantsData } = useGetGrantsForBlackScholes();
  const [isFiltersOpen, setIsFiltersOpen] = useState(true);

  const validationSchema = Yup.object().shape({
    grantStartDate: Yup.date().required("Start date is required"),
    grantEndDate: Yup.date()
      .required("End date is required")
      .min(Yup.ref("grantStartDate"), "End date must be after start date"),
    valuationReportDate: Yup.string().required(
      "Valuation report date is required"
    ),
    optionsExpiryFromPlanMaturity: Yup.boolean(),
    optionsExpiryYears: Yup.number().when("optionsExpiryFromPlanMaturity", {
      is: false,
      then: (schema) =>
        schema
          .min(1, "Expiry years must be at least 1")
          .required("Expiry years is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  const formik = useFormik({
    initialValues: {
      grantStartDate: format(
        new Date(grantScopeData?.startDate || new Date()),
        "yyyy-MM-dd"
      ),
      grantEndDate: format(
        new Date(grantScopeData?.endDate || new Date()),
        "yyyy-MM-dd"
      ),
      valuationReportDate: format(
        new Date(grantScopeData?.valuationDate || new Date()),
        "yyyy-MM-dd"
      ),
      optionsExpiryFromPlanMaturity:
        grantScopeData?.optionsExpiryFromPlanMaturity ?? true,
      optionsExpiryYears: grantScopeData?.optionsExpiryYears || 0,
      expectedLifeCalculationMethod:
        grantScopeData?.expectedLifeCalculationMethod || "AGGREGATED_VESTING",
      filters: grantScopeData?.filters || {},
      filteredGrants: [] as BlackScholesGrantDetails[],
      groupByGrantDateAndStrikePrice:
        (grantScopeData?.groupByGrantDateAndStrikePrice ?? true) as boolean,
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values) => {
      const initialStartDate = grantScopeData?.startDate
        ? format(new Date(grantScopeData.startDate), "yyyy-MM-dd")
        : "";
      const initialEndDate = grantScopeData?.endDate
        ? format(new Date(grantScopeData.endDate), "yyyy-MM-dd")
        : "";

      const hasDatesChanged =
        values.grantStartDate !== initialStartDate ||
        values.grantEndDate !== initialEndDate;

      const hasFiltersChanged =
        JSON.stringify(values.filters) !==
        JSON.stringify(grantScopeData?.filters || {});

      const shouldResetData = hasDatesChanged || hasFiltersChanged;

      onNext({
        startDate: values.grantStartDate,
        endDate: values.grantEndDate,
        valuationDate: new Date(values.valuationReportDate),
        optionsExpiryFromPlanMaturity: values.optionsExpiryFromPlanMaturity,
        optionsExpiryYears: values.optionsExpiryFromPlanMaturity
          ? 0
          : values.optionsExpiryYears,
        expectedLifeCalculationMethod: values.expectedLifeCalculationMethod,
        filters: values.filters,
        grantDatesWithData: shouldResetData
          ? {}
          : grantScopeData?.grantDatesWithData || {},
        filteredGrants: grantsFiltered,
        groupByGrantDateAndStrikePrice:
          values.groupByGrantDateAndStrikePrice || true,
      });
    },
  });

  // 1. Memoize Filter Options (derived from grantsData)
  const filterOptions = useMemo(() => {
    if (!grantsData)
      return {
        uniquePlanIds: [],
        employmentStatusOptions: [],
        employmentTypesOptions: [],
        grantStateOptions: [],
        uniqueSubsidiaryIds: [],
      };

    const data = Array.isArray(grantsData)
      ? grantsData
      : (grantsData as any).grants || [];

    const uniquePlanIds: NameAndId[] = [];
    const employmentStatusOptions: string[] = [];
    const employmentTypesOptions: string[] = [];
    const grantStateOptions: string[] = [];
    const uniqueSubsidiaryIds: NameAndId[] = [];

    data.forEach((grant: any) => {
      const planId = grant.plan?.esopPlanId || grant.planid || grant.plan?.id;
      const planName =
        grant.plan?.planName || grant.planName || grant.plan?.name;

      if (planId && planName && !uniquePlanIds.find((p) => p.id === planId)) {
        uniquePlanIds.push({ id: planId, name: planName });
      }

      const empStatus =
        grant.employee?.employmentStatus ||
        grant.employmentStatus ||
        grant.employee?.status;
      if (empStatus && !employmentStatusOptions.includes(empStatus)) {
        employmentStatusOptions.push(empStatus);
      }

      const empType =
        grant.employee?.employmentType ||
        grant.employmentType ||
        grant.employee?.type;
      if (empType && !employmentTypesOptions.includes(empType)) {
        employmentTypesOptions.push(empType);
      }

      const gState = grant.grantState || grant.state || grant.optionHolderState;
      if (gState && !grantStateOptions.includes(gState)) {
        grantStateOptions.push(gState);
      }

      const compId = grant.company?.id || grant.companyId;
      const compName = grant.company?.name || grant.companyName;
      if (
        compId &&
        compName &&
        !uniqueSubsidiaryIds.find((c) => c.id === compId)
      ) {
        uniqueSubsidiaryIds.push({ id: compId, name: compName });
      }
    });

    return {
      uniquePlanIds,
      employmentStatusOptions,
      employmentTypesOptions,
      grantStateOptions,
      uniqueSubsidiaryIds,
    };
  }, [grantsData]);

  // 2. Memoize Filters Array (derived from options + formik values)
  const filters = useMemo(() => {
    const newFilters: GenericFilterType[] = [];
    const {
      uniquePlanIds,
      employmentStatusOptions,
      employmentTypesOptions,
      grantStateOptions,
      uniqueSubsidiaryIds,
    } = filterOptions;

    if (uniquePlanIds.length > 0) {
      newFilters.push({
        label: "Plans",
        id: "planIds",
        options: uniquePlanIds,
        type: "multiCheckboxNameAndId",
        value: formik.values.filters?.planIds || [],
        onChange: (selectedValues: NameAndId[]) => {
          formik.setFieldValue("filters.planIds", selectedValues);
        },
      });
    }

    if (employmentTypesOptions.length > 0) {
      newFilters.push({
        label: "Employment Type",
        id: "employmentType",
        options: employmentTypesOptions,
        type: "multiCheckbox",
        value: formik.values.filters?.employmentType || [],
        onChange: (selectedValues: string[]) => {
          formik.setFieldValue("filters.employmentType", selectedValues);
        },
      });
    }

    if (employmentStatusOptions.length > 0) {
      newFilters.push({
        label: "Employment Status",
        id: "employeeStatus",
        options: employmentStatusOptions,
        type: "multiCheckbox",
        value: formik.values.filters?.employeeStatus || [],
        onChange: (selectedValues: string[]) => {
          formik.setFieldValue("filters.employeeStatus", selectedValues);
        },
      });
    }

    if (grantStateOptions.length > 0) {
      newFilters.push({
        label: "Grant State",
        id: "grantStates",
        options: grantStateOptions,
        type: "multiCheckbox",
        value: formik.values.filters?.grantStates || [],
        onChange: (selectedValues: string[]) => {
          formik.setFieldValue("filters.grantStates", selectedValues);
        },
      });
    }

    if (uniqueSubsidiaryIds.length > 0) {
      newFilters.push({
        label: "Companies",
        id: "subsidiaryIds",
        options: uniqueSubsidiaryIds,
        type: "multiCheckboxNameAndId",
        value: formik.values.filters?.subsidiaryIds || [],
        onChange: (selectedValues: NameAndId[]) => {
          formik.setFieldValue("filters.subsidiaryIds", selectedValues);
        },
      });
    }

    return newFilters;
  }, [filterOptions, formik.values.filters, formik.setFieldValue]);

  // 3. Memoize Filtered Grants (derived from grantsData + formik values)
  const grantsFiltered = useMemo(() => {
    // Check if form values match initial values (Edit Mode Optimization)
    const initialStartDate = grantScopeData?.startDate
      ? format(new Date(grantScopeData.startDate), "yyyy-MM-dd")
      : "";
    const initialEndDate = grantScopeData?.endDate
      ? format(new Date(grantScopeData.endDate), "yyyy-MM-dd")
      : "";

    const isDatesSame =
      formik.values.grantStartDate === initialStartDate &&
      formik.values.grantEndDate === initialEndDate;

    // Helper to normalize filters for comparison (ignore empty arrays/nulls)
    const normalizeFilters = (filters: any) => {
      if (!filters) return {};
      const normalized: any = {};
      Object.keys(filters).forEach((key) => {
        const value = filters[key];
        if (Array.isArray(value) && value.length > 0) {
          // Sort arrays to ensure order doesn't affect comparison
          if (typeof value[0] === "string") {
            normalized[key] = [...value].sort();
          } else if (typeof value[0] === "object" && "id" in value[0]) {
            normalized[key] = [...value].sort((a, b) =>
              a.id.localeCompare(b.id)
            );
          } else {
            normalized[key] = value;
          }
        }
      });
      // Sort keys to ensure object order doesn't affect comparison
      return Object.keys(normalized)
        .sort()
        .reduce((obj: any, key) => {
          obj[key] = normalized[key];
          return obj;
        }, {});
    };

    const isFiltersSame =
      JSON.stringify(normalizeFilters(formik.values.filters)) ===
      JSON.stringify(normalizeFilters(grantScopeData?.filters));

    // If nothing changed and we have server data, use it
    if (
      isDatesSame &&
      isFiltersSame &&
      grantScopeData?.filteredGrants &&
      grantScopeData.filteredGrants.length > 0
    ) {
      return grantScopeData.filteredGrants;
    }

    if (!grantsData) return [];
    let data = Array.isArray(grantsData)
      ? grantsData
      : (grantsData as any).grants || [];

    // Date Filter
    if (formik.values.grantStartDate && formik.values.grantEndDate) {
      const start = new Date(formik.values.grantStartDate);
      const end = new Date(formik.values.grantEndDate);
      data = data.filter((g: BlackScholesGrantDetails) => {
        if (!g.dateOfGrant) return true;
        const d = new Date(g.dateOfGrant);
        return d >= start && d <= end;
      });
    }

    // Other Filters
    const activeFilters = formik.values.filters || {};

    if (activeFilters.planIds && activeFilters.planIds.length > 0) {
      const ids = activeFilters.planIds.map((p: any) => p.id);
      data = data.filter((g: BlackScholesGrantDetails) => {
        const planId = g.plan?.id;
        return ids.includes(planId);
      });
    }

    if (
      activeFilters.employmentType &&
      activeFilters.employmentType.length > 0
    ) {
      data = data.filter((g: BlackScholesGrantDetails) => {
        const empType = g.employee?.type;
        return activeFilters?.employmentType?.includes(empType);
      });
    }

    if (
      activeFilters.employeeStatus &&
      activeFilters.employeeStatus.length > 0
    ) {
      data = data.filter((g: BlackScholesGrantDetails) => {
        const empStatus = g.employee?.status;
        return activeFilters?.employeeStatus?.includes(empStatus);
      });
    }

    if (activeFilters.grantStates && activeFilters.grantStates.length > 0) {
      data = data.filter((g: BlackScholesGrantDetails) => {
        const gState = g.state;
        return activeFilters?.grantStates?.includes(gState);
      });
    }

    if (activeFilters.subsidiaryIds && activeFilters.subsidiaryIds.length > 0) {
      const ids = activeFilters.subsidiaryIds.map((c: any) => c.id);
      data = data.filter((g: BlackScholesGrantDetails) => {
        const compId = g.company?.id;
        return ids.includes(compId);
      });
    }

    return data;
  }, [
    grantsData,
    formik.values.grantStartDate,
    formik.values.grantEndDate,
    formik.values.filters,
  ]);

  const summary = useMemo(() => {
    const filtered = grantsFiltered;

    const uniqueEmployees = new Set(
      filtered.map((g: BlackScholesGrantDetails) => g.employee?.id)
    ).size;
    const totalOptions = filtered.reduce(
      (sum: number, g: BlackScholesGrantDetails) =>
        sum + (Number(g.grantedOptions) || 0),
      0
    );
    const uniquePlans = new Set(
      filtered.map((g: BlackScholesGrantDetails) => g.plan?.id)
    ).size;

    return {
      grants: filtered.length,
      employees: uniqueEmployees,
      options: totalOptions,
      plans: uniquePlans,
    };
  }, [
    grantsFiltered,
    formik.values.filters,
    formik.values.grantStartDate,
    formik.values.grantEndDate,
  ]);

  return (
    <Box className="w-full">
      <VStack>
        <VStack className="justify-between gap-1 pb-3 pl-6 font-medium border-b-[0.5px] pt-7">
          <HStack className="flex justify-between font-semibold text-lg1 text-black-501">
            Grant Scope
          </HStack>
          <p className="text-gray-500 text-sm mt-1">
            Define the scope of grants to be valued
          </p>
        </VStack>

        <form className="w-full" onSubmit={(e) => e.preventDefault()}>
          <VStack className="gap-6 w-full p-6">
            <HStack className="gap-6 w-full">
              <Box className="flex-1">
                <Label className="required text-sm font-normal">
                  Grant Start Date
                </Label>
                <Input
                  type="date"
                  name="grantStartDate"
                  value={formik.values.grantStartDate}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.grantStartDate &&
                  formik.errors.grantStartDate && (
                    <Error text={formik.errors.grantStartDate} />
                  )}
              </Box>
              <Box className="flex-1">
                <Label className="required text-sm font-normal">
                  Grant End Date
                </Label>
                <Input
                  type="date"
                  name="grantEndDate"
                  value={formik.values.grantEndDate}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.grantEndDate && formik.errors.grantEndDate && (
                  <Error text={formik.errors.grantEndDate} />
                )}
              </Box>
            </HStack>
            <HStack className="gap-6 w-full">
              <Box className="flex-1">
                <Label className="required text-sm font-normal">
                  Valuation Report Date
                </Label>
                <Input
                  name="valuationReportDate"
                  value={formik.values.valuationReportDate}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  type="date"
                />
                {formik.touched.valuationReportDate &&
                  formik.errors.valuationReportDate && (
                    <Error text={formik.errors.valuationReportDate} />
                  )}
              </Box>
              <Box className="flex-1">
                <HStack className="gap-4 justify-between items-center h-full">
                  <SwitchButton
                    className="font-medium"
                    label=""
                    leftLabel="Use plan active employee expiry policy"
                    value={formik.values?.optionsExpiryFromPlanMaturity}
                    onClick={() =>
                      formik.setFieldValue(
                        "optionsExpiryFromPlanMaturity",
                        !formik.values.optionsExpiryFromPlanMaturity
                      )
                    }
                  />
                </HStack>
              </Box>
            </HStack>
            <HStack className="gap-6 w-full">
              <Box className="flex-1">
                <Label className="mb-2 text-sm font-normal">
                  Grouping Method
                </Label>
                <div className="flex gap-12 items-center">
                  <HStack className="items-center gap-1">
                    <input
                      type="radio"
                      name="groupByGrantDateAndStrikePrice"
                      className="accent-orange-501 outline-hidden mr-1 cursor-pointer"
                      checked={
                        formik.values.groupByGrantDateAndStrikePrice === true
                      }
                      onChange={() =>
                        formik.setFieldValue(
                          "groupByGrantDateAndStrikePrice",
                          true
                        )
                      }
                    />
                    <Label className="mt-2 text-sm font-normal whitespace-nowrap">
                      Group by Grant Date & Strike Price
                    </Label>
                  </HStack>
                  <HStack className="items-center gap-1">
                    <input
                      type="radio"
                      name="groupByGrantDateAndStrikePrice"
                      className="accent-orange-501 outline-hidden mr-1 cursor-pointer"
                      checked={
                        formik.values.groupByGrantDateAndStrikePrice === false
                      }
                      onChange={() =>
                        formik.setFieldValue(
                          "groupByGrantDateAndStrikePrice",
                          false
                        )
                      }
                    />
                    <Label className="mt-2 text-sm font-normal whitespace-nowrap">
                      Group by Grant Date
                    </Label>
                  </HStack>
                </div>
              </Box>
              {formik.values?.optionsExpiryFromPlanMaturity ? (
                <Box className="flex-1" />
              ) : (
                <Box className="flex-1">
                  <Label className="required text-sm font-normal">
                    Options Expiry (No of Years)
                  </Label>
                  <Input
                    type="number"
                    name="optionsExpiryYears"
                    value={formik.values.optionsExpiryYears}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.optionsExpiryYears &&
                    formik.errors.optionsExpiryYears && (
                      <Error text={formik.errors.optionsExpiryYears} />
                    )}
                </Box>
              )}
            </HStack>
            <Box className="w-full">
              <Label className="mb-2 text-sm font-normal">
                Expected Life Calculation Method
              </Label>
              <div className="flex gap-12 items-center">
                {(["AGGREGATED_VESTING", "TRANCHE_WISE"] as const).map(
                  (method) => (
                    <HStack className="items-center gap-1" key={method}>
                      <input
                        type="radio"
                        name="expectedLifeCalculationMethod"
                        className="accent-orange-501 outline-hidden mr-1 cursor-pointer"
                        checked={
                          formik.values.expectedLifeCalculationMethod === method
                        }
                        onChange={() =>
                          formik.setFieldValue(
                            "expectedLifeCalculationMethod",
                            method
                          )
                        }
                      />
                      <Label className="mt-2 text-sm font-normal whitespace-nowrap">
                        {EXPECTED_LIFE_CALCULATION_METHOD_LABELS[method]}
                      </Label>
                    </HStack>
                  )
                )}
              </div>
            </Box>
            <CollapsibleComponent
              label="Filters"
              labelDetail=""
              isOpen={isFiltersOpen}
              onClick={() => setIsFiltersOpen(!isFiltersOpen)}
            >
              {filters.length > 0 && (
                <GenericFilterBar filters={filters} disableUpdate={false} />
              )}
            </CollapsibleComponent>{" "}
            {/* Summary Section */}
            <Box className="bg-blue-50 p-6 rounded-lg border border-blue-100 mt-2">
              <HStack className="justify-between text-center divide-x divide-blue-200">
                <VStack className="flex-1 px-4">
                  <span className="text-gray-500 text-sm mb-1">
                    Grants Selected
                  </span>
                  <span className="text-2xl font-bold text-gray-900">
                    {summary.grants}
                  </span>
                </VStack>
                <VStack className="flex-1 px-4">
                  <span className="text-gray-500 text-sm mb-1">Employees</span>
                  <span className="text-2xl font-bold text-gray-900">
                    {summary.employees}
                  </span>
                </VStack>
                <VStack className="flex-1 px-4">
                  <span className="text-gray-500 text-sm mb-1">
                    Total Options
                  </span>
                  <span className="text-2xl font-bold text-gray-900">
                    {summary.options.toLocaleString()}
                  </span>
                </VStack>
              </HStack>
            </Box>
            <Box className="flex justify-end mt-4">
              <ButtonPrimary onClick={() => formik.handleSubmit()}>
                Continue
              </ButtonPrimary>
            </Box>
          </VStack>
        </form>
      </VStack>
    </Box>
  );
};
